import IAppEvents from '@app/lib/IAppEvents';
import IAppLogger from '@app/lib/IAppLogger';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import { NO_ACTIVE_CONTRACT, NO_SERVICES_FOUND } from './constants/auth';
import {
  AuthEvents,
  AuthCanSignoutHandler,
  AuthSignoutHandler,
  AuthUser,
} from './types';
import getCurrentServices from './lib/getCurrentServices';
import getCurrentUser from './lib/getCurrentUser';
import { authState } from './state';
import {
  probeBffUser,
  fetchBffToken,
  redirectToBffLogin,
  redirectToBffLogout,
} from './lib/bffSession';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import getWebPhoneToken from './lib/getWebPhoneToken';
import { UserSubscriptionManager } from './lib/UserSubscriptionManager';
import { hasValidSeat } from '@constants/user';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import type { StepHandle } from '@servicetitan/journey';
import {
  createSessionJourney,
  CreateSessionStep,
  CreateSessionStepError,
  runCreateSessionStep,
  startCreateSessionJourney,
} from '@lib/journeys/createSessionJourney';
import stampedFetchOptions, {
  failOnClientErrors,
  ignoreRetriedFailures,
} from '@lib/journeys/stampedFetchOptions';

const AUTH_FORCE_LOGIN_HOURS = 10;

export class AuthModule extends AppModule {
  private readonly subscriptionManager: UserSubscriptionManager;
  override events = [
    AuthEvents.SIGNIN,
    AuthEvents.SIGNOUT,
    AuthEvents.USER_UPDATED,
  ];
  private signoutHandlers: Record<string, AuthSignoutHandler> = {};
  private canSignoutHandlers: Record<string, AuthCanSignoutHandler> = {};
  private bffTokenFetchPromise: Promise<string> | null = null;
  private sessionExpiredShown = false;

  constructor(events: IAppEvents, logger: IAppLogger) {
    super(events, logger);

    this.subscriptionManager = new UserSubscriptionManager(
      this.app.logger,
      this.app.events,
    );
  }

  override preload(): void {
    // Wrap event handlers in try-catch for defensive programming.
    // Even though internal methods have error handling, this provides
    // an extra safety layer to prevent unhandled promise rejections
    // that could propagate to the event system and cause app instability.
    this.app.events.on('app.started', async () => {
      // Mark loading before the async session probe below so SigninPage shows
      // its spinner instead of flashing the "Sign in" button while we check
      // whether an existing BFF session cookie already signs the user in.
      authState.loading = true;
      authState.loadingState = 'signin';
      startCreateSessionJourney();
      try {
        await runCreateSessionStep(
          CreateSessionStep.LoadSession,
          async (step) => {
            // The BFF answers 200 { isAuthenticated: false } when nobody is
            // signed in, so any other non-OK response is a failure.
            const bffUser = await probeBffUser(
              stampedFetchOptions(step, failOnClientErrors),
            );
            if (bffUser === null) {
              // Signing in through Enterprise Hub is up to the user. An
              // error from the probe has already closed the journey as bad.
              createSessionJourney.exclude();
              return;
            }
            await this.setAuthenticated(step);
            if (authState.signedIn) {
              return;
            }
            if (authState.error === NO_ACTIVE_CONTRACT) {
              // Account setup, not an outage.
              createSessionJourney.exclude();
              return;
            }
            if (authState.error === NO_SERVICES_FOUND) {
              // The BFF creates a contact center for an org that has none, so
              // an empty list means it could not build the user's context
              // (see docs/code/critical-user-flows/ccpro-ready-to-take-calls.md).
              step.setAttribute('sign_in_error', 'no-contact-center');
            }
            throw new CreateSessionStepError(
              authState.error ?? 'Sign-in failed',
            );
          },
        );
      } catch (err) {
        this.app.logger.error('Error in app.started handler', err);
      } finally {
        // setAuthenticated() (if it ran) already reset loading in its own
        // finally. If we landed here unauthenticated - or the handler threw
        // before reaching setAuthenticated() - clear it ourselves so
        // SigninPage doesn't stay stuck on the spinner.
        if (!authState.signedIn) {
          authState.loading = false;
          authState.loadingState = 'none';
        }
      }
    });

    this.app.events.on('app.wake', async () => {
      try {
        await this.checkAuthAge();
        await this.subscriptionManager.handleWake();
      } catch (err) {
        this.app.logger.error('Error in app.wake handler', err);
      }
    });
  }

  /**
   * Initiates sign-in via the BFF session path.
   *
   * Performs a top-level navigation to `/bff/login?returnUrl=<current-url>`
   * so the server-side BFF handles the OpenID Connect flow. No tokens are
   * redeemed in the browser.
   *
   * @param returnUrl - The URL to return to after the BFF completes sign-in.
   *   Defaults to the full current SPA URL.
   */
  public signinViaBff(returnUrl?: string): void {
    redirectToBffLogin(returnUrl);
  }

  /**
   * Gets the signed-in status of the user.
   *
   * @returns {boolean} - Returns `true` if the user is signed in,
   * otherwise `false`.
   */
  public get signedIn(): boolean {
    return authState.signedIn;
  }

  /**
   * Signs the user out by executing any registered signout handlers and
   * updating the authentication state, then logging out through the BFF.
   *
   * @returns {Promise<void>} A promise that resolves when the signout process
   * is complete.
   */
  public async signout(): Promise<void> {
    if (authState.loading && authState.loadingState === 'signout') {
      return;
    }

    authState.loading = true;
    authState.loadingState = 'signout';
    authState.error = undefined;

    if (!(await this.canSignOut())) {
      authState.loading = false;
      authState.loadingState = 'none';
      return;
    }

    const handlerStartTime = Date.now();

    this.app.logger.info('Signout handlers started', {
      handlerCount: Object.keys(this.signoutHandlers).length,
      handlers: Object.keys(this.signoutHandlers),
    });

    // Execute all handlers even if some fail - wrap each in try-catch
    // to ensure cleanup handlers after a failed one still execute and
    // setNotAuthenticated() always runs to complete local signout
    for (const [key, handler] of Object.entries(this.signoutHandlers)) {
      try {
        await handler();
      } catch (err) {
        this.app.logger.error(`Signout handler '${key}' failed`, err);
      }
    }

    this.app.logger.info('Signout handlers completed', {
      duration: Date.now() - handlerStartTime,
    });

    this.setNotAuthenticated();

    // Log out through the BFF so it clears the server-side session
    // (cookie + token store) and drives the upstream OIDC end-session.
    // This navigates away, so nothing after it runs on success.
    try {
      await redirectToBffLogout();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to complete BFF logout';
      this.app.logger.error('Signout failed at BFF logout', {
        error: errorMessage,
      });
      authState.loading = false;
      authState.loadingState = 'none';
      authState.error = errorMessage;
      this.app.events.emit('auth.signout.failed', {
        error: errorMessage,
        locallySignedOut: true,
      });
    }
  }

  /**
   * Registers a handler that determines if the user can sign out.
   *
   * @param key - A unique identifier for the handler.
   * @param handler - The handler function that will be called to check if the
   * user can sign out.
   */
  public registerCanSignoutHandler(
    key: string,
    handler: AuthCanSignoutHandler,
  ): void {
    this.canSignoutHandlers[key.toLowerCase()] = handler;
  }

  /**
   * Registers a signout handler for a given key.
   *
   * @param key - The key associated with the signout handler.
   * @param handler - The handler function to be called on signout.
   * @returns void
   */
  public registerSignoutHandler(
    key: string,
    handler: AuthSignoutHandler,
  ): void {
    this.signoutHandlers[key.toLowerCase()] = handler;
  }

  /**
   * Unregisters a handler for the sign-out event.
   *
   * @param key - The key associated with the handler to be removed. The key is
   * case-insensitive.
   */
  public unregisterCanSignoutHandler(key: string): void {
    delete this.canSignoutHandlers[key.toLowerCase()];
  }

  /**
   * Unregisters a signout handler by its key.
   *
   * @param key - The key of the signout handler to unregister.
   */
  public unregisterSignoutHandler(key: string): void {
    delete this.signoutHandlers[key.toLowerCase()];
  }

  /**
   * Gets the current authenticated user.
   *
   * @returns {AuthUser | undefined} The authenticated user if available,
   * otherwise undefined.
   */
  public get user(): AuthUser | undefined {
    return authState.user;
  }

  /**
   * Checks if the user has access to a specific tenant.
   */
  public hasAccessToTenant(tenantId: string): boolean {
    return authState.user?.org.tenants.some((t) => t.id === tenantId) ?? false;
  }

  /**
   * Retrieves a bridged access token from the BFF.
   *
   * The token is held in-memory only — NEVER written to any storage.
   * Concurrent callers share the same in-flight fetch rather than each
   * issuing their own request to `/bff/token`.
   *
   * @returns {Promise<string>} A promise that resolves to the current access
   * token.
   */
  public async getCurrentToken(): Promise<string> {
    this.bffTokenFetchPromise ??= fetchBffToken().finally(() => {
      this.bffTokenFetchPromise = null;
    });
    const token = await this.bffTokenFetchPromise;
    authState.token = token;
    return token;
  }

  /**
   * Retrieves the current service ID from the authentication state.
   *
   * @returns {string} The current service ID if available, otherwise an empty
   * string.
   */
  public getCurrentServiceId(): string {
    return authState.serviceId || '';
  }

  /**
   * Resets the authentication age by updating the `refreshedAt` property
   * of the `authState` object to the current date and time.
   */
  public resetAuthAge(): void {
    authState.refreshedAt = Date.now();
    this.app.logger.info('Auth age reset');
  }

  /**
   * Retrieves a new web phone token for the user.
   *
   * This method fetches a new web phone token and updates the user's
   * `webPhoneToken` property in the authentication state.
   *
   * @returns {Promise<string>} A promise that resolves to the new web phone
   * token.
   */
  public async getNewWebPhoneToken(): Promise<string> {
    const user = authState.user;
    if (!user || !hasValidSeat(user.seatLevel)) {
      const error = new Error(
        'Cannot fetch web phone token without a valid seat',
      );
      this.app.logger.warn(error.message, {
        seatLevel: user?.seatLevel,
      });
      throw error;
    }

    try {
      const token = await getWebPhoneToken();
      user.webPhoneToken = token;
      this.app.events.emit(AuthEvents.USER_UPDATED, user);
      return token;
    } catch (err) {
      this.app.logger.error('Error getting new web phone token', err);
      throw new Error(extractErrorMessage(err));
    }
  }

  /**
   * Checks if the user can sign out by invoking all registered sign-out
   * handlers.
   *
   * This method iterates through all handlers in `canSignoutHandlers` and
   * executes them. If any handler returns `false`, the method returns `false`,
   * indicating that the user cannot sign out. If all handlers return `true`,
   * the method returns `true`, indicating that the user can sign out.
   *
   * Handlers are wrapped in try-catch for defensive programming:
   * - External module code may have bugs that cause exceptions
   * - Exceptions are treated as "cannot sign out" (safer default)
   * - Prevents state corruption and unhandled promise rejections
   * - Clear logging helps identify broken handlers for debugging
   *
   * @returns {Promise<boolean>} A promise that resolves to `true` if the user
   *                             can sign out, or `false` if any handler
   *                             prevents the sign-out or throws an exception.
   */
  private async canSignOut(): Promise<boolean> {
    const canSignoutHandlers = Object.entries(this.canSignoutHandlers);
    for (const [key, handler] of canSignoutHandlers) {
      try {
        if (!(await handler())) {
          return false;
        }
      } catch (err) {
        this.app.logger.error(
          `canSignout handler '${key}' threw an exception`,
          err,
        );
        return false;
      }
    }

    return true;
  }

  /**
   * Sets the authentication state for the application.
   *
   * If an error occurs during the process, it:
   * - Logs the error.
   * - Resets the authentication.
   *
   * @param step - Journey step whose stamp binds the session requests.
   * @returns {Promise<void>} A promise that resolves when the authentication
   * state is set.
   * @throws {Error} Throws an error if no services are found or if the
   * user does not have an active contract.
   */
  private async setAuthenticated(step?: StepHandle): Promise<void> {
    if (authState.signedIn) {
      return;
    }

    authState.loading = true;
    authState.loadingState = 'signin';
    try {
      // Signed-in state is derived from the /bff/authinfo probe. The token
      // bridge (getCurrentToken → fetchBffToken) fills authState.token on
      // demand; it is never written to storage.
      authState.token = '';

      if (!authState.serviceId) {
        const services = await getCurrentServices(
          stampedFetchOptions(step, ignoreRetriedFailures),
        );
        if (services.length === 0) {
          throw new Error(NO_SERVICES_FOUND);
        }
        authState.serviceId = services[0].id;
        authState.serviceName = services[0].name;
      }
      authState.user = await getCurrentUser(
        false,
        stampedFetchOptions(step, ignoreRetriedFailures),
      );
      if (!authState.user.org.hasActiveContract) {
        throw new Error(NO_ACTIVE_CONTRACT);
      }
      authState.signedIn = true;
      this.app.events.emit(AuthEvents.SIGNIN, this.user);
      void this.subscriptionManager.setup(authState.user.id);
    } catch (err) {
      this.app.logger.error('Error setting auth', err);
      authState.error = extractErrorMessage(err);
      authState.serviceId = '';
      authState.serviceName = '';
      authState.token = '';
      authState.user = undefined;
      authState.signedIn = false;
      authState.refreshedAt = 0;
    } finally {
      authState.loading = false;
      authState.loadingState = 'none';
    }
  }

  /**
   * Sets the authentication state to "not authenticated".
   */
  private setNotAuthenticated(): void {
    if (!authState.signedIn) {
      return;
    }

    authState.serviceId = '';
    authState.serviceName = '';
    authState.token = '';
    authState.user = undefined;
    authState.signedIn = false;
    authState.refreshedAt = 0;

    this.subscriptionManager.cleanup();

    this.app.events.emit(AuthEvents.SIGNOUT);
  }

  /**
   * Checks the authentication age and signs out the user if the age is
   * greater than the AUTH_FORCE_LOGIN_HOURS constant.
   */
  private async checkAuthAge(): Promise<void> {
    // CCP-40136: EH now auto re-logins on link-out (via Token Server), so the
    // 10-hour proactive logout is no longer needed. This flag bypasses it; the
    // auth-age tracking is slated for full removal in a follow-up (Phase 2).
    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureBypassAuthAgeCheck)
    ) {
      return;
    }

    if (authState.user && this.getAuthAgeInHours > AUTH_FORCE_LOGIN_HOURS) {
      if (!(await this.canSignOut())) {
        if (!this.sessionExpiredShown) {
          this.sessionExpiredShown = true;
          DiagnosticsModule.reportUserError(
            'Your session has expired and you need to sign in again, but something is preventing you from signing out. Please sign out manually and sign in again.',
            {
              priority: 'passive',
            },
          );
        }
        return;
      }

      this.sessionExpiredShown = false;

      this.app.logger.info('Auth is stale, signing out');
      this.signout();
    }
  }

  /**
   * Calculates the age of the authentication session in hours.
   *
   * @returns {number} The age of the authentication session in hours.
   */
  private get getAuthAgeInHours(): number {
    return (Date.now() - authState.refreshedAt) / 1000 / 60 / 60;
  }
}

export default new AuthModule(appManager.events, appManager.logger('Auth'));
export type { AuthUser } from './types';
